import type {
	QuizAnswerRole,
	QuizQuestionFormat,
} from "./quiz-question-format";
import {
	quizGradeResultFromScore,
	type QuizGradePart,
	type QuizGradeResult,
} from "./quiz-grade-result";
import type { QuizResponsePayload } from "./quiz-response-payload";

export type { QuizResponsePayload as GradeQuizResponsePayload };
export type {
	QuizGradeFeedback,
	QuizGradeMethod,
	QuizGradeOutcome,
	QuizGradePart,
	QuizGradeResult,
} from "./quiz-grade-result";
export {
	clampUnitInterval,
	outcomeFromScore,
	quizGradeResultFromScore,
} from "./quiz-grade-result";

export interface GradeQuizAnswerRow {
	id: string;
	answerText: string;
	role: QuizAnswerRole;
	displayOrder?: number | null;
	matchKey?: string | null;
	isCorrect?: boolean;
}

export interface GradeQuizQuestionInput {
	format: QuizQuestionFormat;
	formatConfig?: Record<string, unknown> | null;
	answers: GradeQuizAnswerRow[];
}

function normalizeAnswerText(value: string, caseSensitive: boolean): string {
	const trimmed = value.trim().replace(/\s+/g, " ");
	return caseSensitive ? trimmed : trimmed.toLowerCase();
}

function similarityRatio(a: string, b: string): number {
	if (a.length === 0 && b.length === 0) {
		return 1;
	}
	if (a.length === 0 || b.length === 0) {
		return 0;
	}
	const matrix = Array.from({ length: a.length + 1 }, () =>
		Array.from({ length: b.length + 1 }, () => 0),
	);
	for (let i = 0; i <= a.length; i++) {
		matrix[i][0] = i;
	}
	for (let j = 0; j <= b.length; j++) {
		matrix[0][j] = j;
	}
	for (let i = 1; i <= a.length; i++) {
		for (let j = 1; j <= b.length; j++) {
			const cost = a[i - 1] === b[j - 1] ? 0 : 1;
			matrix[i][j] = Math.min(
				matrix[i - 1][j] + 1,
				matrix[i][j - 1] + 1,
				matrix[i - 1][j - 1] + cost,
			);
		}
	}
	const distance = matrix[a.length][b.length];
	return 1 - distance / Math.max(a.length, b.length);
}

function setsEqual(left: Set<string>, right: Set<string>): boolean {
	if (left.size !== right.size) {
		return false;
	}
	for (const value of left) {
		if (!right.has(value)) {
			return false;
		}
	}
	return true;
}

function orderedAnswerIds(
	answers: GradeQuizAnswerRow[],
	role: QuizAnswerRole,
): string[] {
	return [...answers]
		.filter((answer) => answer.role === role)
		.sort(
			(a, b) =>
				(a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
				(b.displayOrder ?? Number.MAX_SAFE_INTEGER),
		)
		.map((answer) => answer.id);
}

function gradeMultipleChoiceSingle(
	answers: GradeQuizAnswerRow[],
	response: Extract<
		QuizResponsePayload,
		{ format: "MULTIPLE_CHOICE_SINGLE" }
	>,
): QuizGradeResult {
	const selected = answers.find(
		(answer) => answer.id === response.selectedAnswerId,
	);
	let score = 0;
	if (selected?.role === "NONE_OF_THESE") {
		score = answers.some((answer) => answer.role === "CORRECT") ? 0 : 1;
	} else if (selected?.role === "CORRECT" || selected?.isCorrect === true) {
		score = 1;
	}
	return quizGradeResultFromScore({
		score,
		method: "exact",
		matchedAnswerId: selected?.id ?? response.selectedAnswerId,
	});
}

function gradeMultipleChoiceMulti(
	answers: GradeQuizAnswerRow[],
	response: Extract<QuizResponsePayload, { format: "MULTIPLE_CHOICE_MULTI" }>,
): QuizGradeResult {
	const correctIds = new Set(
		answers
			.filter((answer) => answer.role === "CORRECT")
			.map((answer) => answer.id),
	);
	const selectedIds = new Set(response.selectedAnswerIds);
	const noneId = answers.find(
		(answer) => answer.role === "NONE_OF_THESE",
	)?.id;
	if (noneId && selectedIds.has(noneId)) {
		const score = correctIds.size === 0 ? 1 : 0;
		return quizGradeResultFromScore({
			score,
			method: "structural",
			matchedAnswerId: noneId,
			parts: [
				{
					key: noneId,
					outcome: score === 1 ? "correct" : "incorrect",
					score,
				},
			],
		});
	}
	const allId = answers.find((answer) => answer.role === "ALL_OF_THESE")?.id;
	if (allId && selectedIds.has(allId)) {
		const expected = new Set([...correctIds, allId]);
		const score = setsEqual(expected, selectedIds) ? 1 : 0;
		return quizGradeResultFromScore({
			score,
			method: "structural",
			matchedAnswerId: allId,
		});
	}

	let intersection = 0;
	for (const id of selectedIds) {
		if (correctIds.has(id)) {
			intersection += 1;
		}
	}
	const union = new Set([...correctIds, ...selectedIds]);
	const score = union.size === 0 ? 1 : intersection / Math.max(union.size, 1);

	const parts: QuizGradePart[] = [...union].map((id) => {
		const inCorrect = correctIds.has(id);
		const inSelected = selectedIds.has(id);
		const partScore = inCorrect && inSelected ? 1 : 0;
		return {
			key: id,
			outcome:
				partScore === 1
					? "correct"
					: inCorrect || inSelected
						? "incorrect"
						: "incorrect",
			score: partScore,
		};
	});

	return quizGradeResultFromScore({
		score,
		method: "structural",
		matchedAnswerId: response.selectedAnswerIds[0] ?? null,
		parts,
	});
}

function gradeFillInBlank(
	question: GradeQuizQuestionInput,
	answers: GradeQuizAnswerRow[],
	response: Extract<QuizResponsePayload, { format: "FILL_IN_BLANK" }>,
): QuizGradeResult {
	const blankIds = orderedAnswerIds(answers, "BLANK_FILL");
	const expected = blankIds.map((id) => {
		const row = answers.find((answer) => answer.id === id);
		return row ? normalizeAnswerText(row.answerText, false) : "";
	});
	const actual = response.blankValues.map((value) =>
		normalizeAnswerText(value, false),
	);
	if (expected.length === 0) {
		return quizGradeResultFromScore({
			score: actual.length === 0 ? 1 : 0,
			method: "structural",
		});
	}
	if (expected.length !== actual.length) {
		return quizGradeResultFromScore({
			score: 0,
			method: "structural",
			matchedAnswerId: blankIds[0] ?? null,
		});
	}

	const blankOrderMatters =
		question.formatConfig?.blankOrderMatters !== false;
	const parts: QuizGradePart[] = [];

	if (blankOrderMatters) {
		let sum = 0;
		for (let index = 0; index < expected.length; index++) {
			const partScore = expected[index] === actual[index] ? 1 : 0;
			sum += partScore;
			parts.push({
				key: blankIds[index] ?? String(index),
				outcome: partScore === 1 ? "correct" : "incorrect",
				score: partScore,
			});
		}
		return quizGradeResultFromScore({
			score: sum / expected.length,
			method: "structural",
			matchedAnswerId: blankIds[0] ?? null,
			parts,
		});
	}

	// Unordered: score by greedy multiset match for mean; parts keyed by blank id order.
	const remaining = [...actual];
	let sum = 0;
	for (let index = 0; index < expected.length; index++) {
		const want = expected[index];
		const foundAt = remaining.indexOf(want);
		const partScore = foundAt >= 0 ? 1 : 0;
		if (foundAt >= 0) {
			remaining.splice(foundAt, 1);
		}
		sum += partScore;
		parts.push({
			key: blankIds[index] ?? String(index),
			outcome: partScore === 1 ? "correct" : "incorrect",
			score: partScore,
		});
	}
	return quizGradeResultFromScore({
		score: sum / expected.length,
		method: "structural",
		matchedAnswerId: blankIds[0] ?? null,
		parts,
	});
}

function gradeTypeAnswer(
	question: GradeQuizQuestionInput,
	answers: GradeQuizAnswerRow[],
	response: Extract<QuizResponsePayload, { format: "TYPE_ANSWER" }>,
): QuizGradeResult {
	const fuzzyThreshold =
		typeof question.formatConfig?.fuzzyThreshold === "number"
			? question.formatConfig.fuzzyThreshold
			: 0.85;
	const caseSensitive = question.formatConfig?.caseSensitive === true;
	const submitted = normalizeAnswerText(response.text, caseSensitive);
	const accepted = answers.filter(
		(answer) => answer.role === "CORRECT" || answer.role === "BLANK_FILL",
	);
	let matchedAnswerId: string | null = null;
	for (const answer of accepted) {
		const candidate = normalizeAnswerText(answer.answerText, caseSensitive);
		if (similarityRatio(submitted, candidate) >= fuzzyThreshold) {
			matchedAnswerId = answer.id;
			break;
		}
	}
	return quizGradeResultFromScore({
		score: matchedAnswerId ? 1 : 0,
		method: "fuzzy",
		matchedAnswerId,
	});
}

function gradeOrderedAnswers(
	answers: GradeQuizAnswerRow[],
	response: Extract<
		QuizResponsePayload,
		{ format: "WORD_REORDER" } | { format: "RANK_ORDER" }
	>,
	role: QuizAnswerRole,
): QuizGradeResult {
	const expected = orderedAnswerIds(answers, role);
	const score =
		expected.length === response.orderedAnswerIds.length &&
		expected.every((id, index) => id === response.orderedAnswerIds[index])
			? 1
			: 0;
	return quizGradeResultFromScore({
		score,
		method: "exact",
		matchedAnswerId: response.orderedAnswerIds[0] ?? expected[0] ?? null,
	});
}

function gradePairMatching(
	answers: GradeQuizAnswerRow[],
	response: Extract<QuizResponsePayload, { format: "PAIR_MATCHING" }>,
): QuizGradeResult {
	const canonical = new Map<string, string>();
	for (const left of answers.filter(
		(answer) => answer.role === "PAIR_LEFT",
	)) {
		const right = answers.find(
			(answer) =>
				answer.role === "PAIR_RIGHT" &&
				answer.matchKey === left.matchKey,
		);
		if (right) {
			canonical.set(left.id, right.id);
		}
	}
	if (canonical.size === 0) {
		return quizGradeResultFromScore({ score: 0, method: "structural" });
	}
	let hits = 0;
	const parts: QuizGradePart[] = [];
	for (const [leftId, rightId] of canonical) {
		const pair = response.pairs.find((entry) => entry.leftId === leftId);
		const partScore = pair?.rightId === rightId ? 1 : 0;
		hits += partScore;
		parts.push({
			key: leftId,
			outcome: partScore === 1 ? "correct" : "incorrect",
			score: partScore,
		});
	}
	return quizGradeResultFromScore({
		score: hits / canonical.size,
		method: "structural",
		matchedAnswerId: response.pairs[0]?.leftId ?? null,
		parts,
	});
}

export function gradeQuizResponse(
	question: GradeQuizQuestionInput,
	response: QuizResponsePayload,
): QuizGradeResult {
	if (question.format !== response.format) {
		return quizGradeResultFromScore({ score: 0, method: "exact" });
	}

	switch (response.format) {
		case "MULTIPLE_CHOICE_SINGLE":
			return gradeMultipleChoiceSingle(question.answers, response);
		case "MULTIPLE_CHOICE_MULTI":
			return gradeMultipleChoiceMulti(question.answers, response);
		case "FILL_IN_BLANK":
			return gradeFillInBlank(question, question.answers, response);
		case "TYPE_ANSWER":
			return gradeTypeAnswer(question, question.answers, response);
		case "WORD_REORDER":
			return gradeOrderedAnswers(
				question.answers,
				response,
				"REORDER_TOKEN",
			);
		case "RANK_ORDER":
			return gradeOrderedAnswers(question.answers, response, "RANK_ITEM");
		case "PAIR_MATCHING":
			return gradePairMatching(question.answers, response);
		default:
			return quizGradeResultFromScore({ score: 0, method: "exact" });
	}
}
